import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Input,
  Button,
  Upload,
  Select,
  InputNumber,
  message,
  Spin,
  Card,
  Space,
  Row,
  Col,
  Divider,
  Switch,
  Alert,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api";
import TinyEditor from "../../components/Editor";
import { SEO_CONFIG } from "../../seo-congig";

const { TextArea } = Input;

const EditCategoryPage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingCategory, setLoadingCategory] = useState(false);
  const [uploading, setUploading] = useState(false);
  const editorRef = useRef(null);
  const [editorContent, setEditorContent] = useState("");
  const [existingBanner, setExistingBanner] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  // State to store validation errors
  const [validationErrors, setValidationErrors] = useState({});
  // State to track if we have validation errors
  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  const handleEditorChange = (content) => {
    setEditorContent(content);
  };

  const categoryTypeOptions = [
    { value: "0", label: "محصول" },
    { value: "1", label: "خبر" },
  ];

  // جستجوی دسته‌بندی‌ها
  const handleSearch = async (value) => {
    try {
      const response = await api.get(`/panel/category`, {
        params: {
          search: value,
          per_page: 15,
        },
      });
      setCategories(response.data.data || []);
    } catch (error) {
      message.error("خطا در جستجوی دسته‌بندی‌ها");
      console.error("خطا در جستجو:", error);
    }
  };

  useEffect(() => {
    const fetchCategory = async () => {
      setLoadingCategory(true);
      try {
        const [categoriesRes] = await Promise.all([
          api.get(`/panel/category?page=all`),
        ]);

        const response = await api.get(`/panel/category/${id}`);
        const data = response?.data?.data;

        // افزودن دسته‌بندی‌های انتخاب‌شده به لیست
        const selectedCats = data?.cats || [];
        const allCategories = [...categoriesRes.data.data, ...selectedCats];
        const uniqueCategories = Array.from(
          new Set(allCategories.map((cat) => cat.id))
        ).map((id) => allCategories.find((cat) => cat.id === id));

        setCategories(uniqueCategories);
        setSelectedCategories(selectedCats);

        form.setFieldsValue({
          name: data.name,
          slug: data.slug,
          description: data.description,
          categories: selectedCats.map((cat) => cat.id),
          categoryType: data?.type?.toString(),
          seo_title: data?.seo_title,
          seo_description: data?.seo_description,
          canonical: data?.canonical,
          follow: data?.follow,
          index: data?.index,
        });
        setEditorContent(data.body);
        setExistingBanner(data.image);
      } catch (error) {
        message.error("خطا در دریافت اطلاعات");
        navigate("/categories");
      } finally {
        setLoadingCategory(false);
      }
    };

    fetchCategory();
  }, [id, form, navigate]);

  // Function to handle form validation errors
  const setServerValidationErrors = (errors) => {
    // Clear any previous validation errors
    setValidationErrors({});
    setHasValidationErrors(false);
    
    if (errors && Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setHasValidationErrors(true);
      
      // Set form field errors
      const formErrors = {};
      
      Object.keys(errors).forEach(field => {
        formErrors[field] = {
          errors: errors[field].map(error => new Error(error))
        };
      });
      
      form.setFields(Object.keys(formErrors).map(field => ({
        name: field,
        errors: errors[field]
      })));
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    // Clear previous validation errors
    setValidationErrors({});
    setHasValidationErrors(false);
    
    try {
      const formData = new FormData();

      const appendIfExists = (key, value) => {
        formData.append(
          key,
          value !== undefined && value !== null ? value : ""
        );
      };

      formData.append("name", values.name);
      formData.append("slug", values.slug);
      formData.append("description", values.description);
      formData.append("body", editorContent);
      formData.append("type", values.categoryType);

      // SEO Fields
      appendIfExists("seo_title", values.seo_title);
      appendIfExists("seo_description", values.seo_description);
      appendIfExists("canonical", values.canonical);
      formData.append("follow", values.follow ? "1" : "0");
      formData.append("index", values.index ? "1" : "0");

      if (values.categories && values.categories.length > 0) {
        values.categories.forEach((categoryId) =>
          formData.append("sub_categories[]", categoryId)
        );
      }

      if (values.banner?.file) {
        formData.append("image", values.banner.file);
      }

      const response = await api.post(
        `/panel/category/${id}?_method=PUT`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      message.success("دسته‌بندی با موفقیت ویرایش شد");
      navigate("/categories");
    } catch (error) {
      // Check if it's a validation error (status 422)
      if (error.response && error.response.status === 422) {
        const errorData = error.response.data;
        if (errorData && errorData.data && errorData.data.errors) {
          setServerValidationErrors(errorData.data.errors);
          message.error("لطفاً خطاهای اعتبارسنجی را برطرف کنید");
        } else {
          message.error("خطا در اعتبارسنجی فرم");
        }
      } else {
        message.error("خطا در ویرایش دسته‌بندی");
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to render validation error messages
  const renderValidationErrors = () => {
    if (!hasValidationErrors) return null;
    
    return (
      <Alert
        message="خطاهای اعتبارسنجی"
        description={
          <ul className="mt-2 list-disc pr-5">
            {Object.keys(validationErrors).map((field) => (
              validationErrors[field].map((error, index) => (
                <li key={`${field}-${index}`}>{error}</li>
              ))
            ))}
          </ul>
        }
        type="error"
        showIcon
        className="mb-4"
      />
    );
  };

  return (
    <Card>
      <div>
        <h2 className="mb-10 text-xl">ویرایش دسته بندی </h2>
        <Spin spinning={loading || loadingCategory}>
          {/* Display validation errors at the top of the form */}
          {renderValidationErrors()}
          
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onFinishFailed={() => {
              message.error("لطفاً فیلدهای ضروری را پر کنید.");
            }}
            scrollToFirstError
            autoComplete="off"
          >
            <Form.Item
              name="name"
              label="عنوان دسته بندی"
              rules={[
                {
                  required: true,
                  message: "لطفاً عنوان را وارد کنید.",
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="slug"
              label="پیوند(لینک این  دسته بندی در وبسایت)"
              rules={[
                {
                  required: true,
                  message: "لطفاً پیوند را وارد کنید.",
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="categories" label="دسته‌بندی‌ها">
                  <Select
                    mode="multiple"
                    placeholder="دسته‌بندی‌ها را انتخاب کنید"
                    allowClear
                    showSearch
                    filterOption={false} // غیرفعال کردن فیلتر داخلی
                    onSearch={handleSearch} // ارسال درخواست جستجو به API
                    options={categories.map((category) => ({
                      value: category.id,
                      label: category.name,
                    }))}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="categoryType" label="نوع دسته بندی">
                  <Select placeholder="نوع دسته بندی را انتخاب کنید">
                    {categoryTypeOptions.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="محتوا"
              required
              validateTrigger={["onChange", "onBlur"]}
              rules={[
                {
                  required: true,
                  validator: (_, value) => {
                    if (!editorContent) {
                      return Promise.reject("لطفاً محتوا را وارد کنید.");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <TinyEditor
                content={editorContent}
                onEditorChange={handleEditorChange}
                model={"Category"}
                height={300}
              />
            </Form.Item>

            <div className="border p-4 rounded-lg mb-4">
              <h3 className="text-xl text-blue-500 text-center mb-4">Media</h3>

              <Form.Item
                name="banner"
                label=" تصویر بنر"
                extra={
                  existingBanner && (
                    <img
                      className="mt-4"
                      src={existingBanner}
                      alt="Current banner"
                      style={{ maxWidth: "200px" }}
                    />
                  )
                }
              >
                <Upload
                  maxCount={1}
                  beforeUpload={() => false}
                  listType="picture"
                >
                  <Button icon={<UploadOutlined />} loading={uploading}>
                    {existingBanner ? " تغییر تصویر " : " انتخاب تصویر "}
                  </Button>
                </Upload>
              </Form.Item>
            </div>

            <Divider>بخش سئو</Divider>
            <Form.Item 
              name="seo_title" 
              label="عنوان سئو"
              help={SEO_CONFIG.title.helpText}            >
              <Input maxLength={SEO_CONFIG.title.maxLength} showCount />
            </Form.Item>

            <Form.Item 
              name="seo_description" 
              label="توضیحات سئو"
              help={SEO_CONFIG.description.helpText}
                          >
              <TextArea rows={4} maxLength={SEO_CONFIG.description.maxLength} showCount />
            </Form.Item>

            <Form.Item name="canonical" label="URL Canonical">
              <Input />
            </Form.Item>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item name="follow" label="Follow" valuePropName="checked">
                  <Switch defaultChecked />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="index" label="Index" valuePropName="checked">
                  <Switch defaultChecked />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  بروزرسانی
                </Button>
                <Button
                  onClick={() => navigate("/categories")}
                  style={{ marginRight: "8px" }}
                >
                  انصراف
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </div>
    </Card>
  );
};

export default EditCategoryPage;